import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Spinner, Badge, Alert, Modal, ListGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../brand-colors';

function SpeakingTopics() {
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(true);
  const [topics, setTopics] = useState([]);
  const [view, setView] = useState('list'); // 'list', 'generate', 'pick', 'detail'

  // Generate state
  const [talkType, setTalkType] = useState('keynote');
  const [generating, setGenerating] = useState(false);
  const [generatedTopics, setGeneratedTopics] = useState([]);

  // Detail view
  const [detailTopic, setDetailTopic] = useState(null);
  const [generatingOneSheet, setGeneratingOneSheet] = useState(false);

  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // No IP warning
  const [showNoIPWarning, setShowNoIPWarning] = useState(false);

  useEffect(() => {
    loadTopics();
  }, []);

  const loadTopics = async () => {
    try {
      const res = await fetch('/api/authority-engineer/speaking/list', {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setTopics(data.topics || []);
        }
      }
    } catch (err) {
      console.error('Error loading topics:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await fetch('/api/authority-engineer/speaking/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ talk_type: talkType })
      });

      const data = await res.json();

      if (data.success) {
        setGeneratedTopics(data.topics || []);
        setView('pick');
      } else if (data.error === 'no_ip') {
        setShowNoIPWarning(true);
      } else {
        alert(data.error || 'Failed to generate topics');
      }
    } catch (err) {
      console.error('Error generating:', err);
      alert('Error generating speaking topics');
    } finally {
      setGenerating(false);
    }
  };

  const handleSelectTopic = async (topic) => {
    try {
      const res = await fetch('/api/authority-engineer/speaking/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...topic,
          talk_type: talkType
        })
      });

      const data = await res.json();

      if (data.success) {
        // View the newly created topic
        await viewTopicDetail(data.topic_id);
        loadTopics();
      } else {
        alert(data.error || 'Failed to create topic');
      }
    } catch (err) {
      console.error('Error creating topic:', err);
      alert('Error creating speaking topic');
    }
  };

  const viewTopicDetail = async (id) => {
    try {
      const res = await fetch(`/api/authority-engineer/speaking/${id}`, {
        credentials: 'include'
      });
      const data = await res.json();

      if (data.success) {
        setDetailTopic(data.topic);
        setView('detail');
      }
    } catch (err) {
      console.error('Error loading topic:', err);
    }
  };

  const handleGenerateOneSheet = async () => {
    if (!detailTopic) return;

    setGeneratingOneSheet(true);
    try {
      const res = await fetch(`/api/authority-engineer/speaking/${detailTopic.id}/generate-onesheet`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await res.json();

      if (data.success) {
        // Refresh the topic detail
        await viewTopicDetail(detailTopic.id);
        loadTopics();
      } else {
        alert(data.error || 'Failed to generate one-sheet');
      }
    } catch (err) {
      console.error('Error generating one-sheet:', err);
      alert('Error generating speaker one-sheet');
    } finally {
      setGeneratingOneSheet(false);
    }
  };

  const handleExport = async (id) => {
    try {
      const res = await fetch(`/api/authority-engineer/speaking/${id}/export`, {
        credentials: 'include'
      });
      const data = await res.json();

      if (data.success) {
        const blob = new Blob([data.markdown], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        alert(data.error || 'Failed to export');
      }
    } catch (err) {
      console.error('Error exporting:', err);
      alert('Error exporting one-sheet');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const res = await fetch(`/api/authority-engineer/speaking/${deleteId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        setTopics(prev => prev.filter(t => t.id !== deleteId));
        if (detailTopic?.id === deleteId) {
          setView('list');
          setDetailTopic(null);
        }
      }
    } catch (err) {
      console.error('Error deleting:', err);
    }

    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const getTalkTypeBadge = (type) => {
    const variants = {
      'keynote': { bg: 'primary', label: 'Keynote' },
      'workshop': { bg: 'success', label: 'Workshop' },
      'webinar': { bg: 'info', label: 'Webinar' },
      'breakout': { bg: 'warning', label: 'Breakout' },
      'panel': { bg: 'secondary', label: 'Panel' }
    };
    const v = variants[type] || { bg: 'secondary', label: type };
    return <Badge bg={v.bg} text={v.bg === 'warning' ? 'dark' : undefined}>{v.label}</Badge>;
  };

  const getStatusBadge = (status) => {
    const variants = {
      'draft': { bg: 'secondary', label: 'Draft' },
      'ready': { bg: 'success', label: 'Ready' },
      'pitched': { bg: 'info', label: 'Pitched' },
      'booked': { bg: 'primary', label: 'Booked' }
    };
    const v = variants[status] || { bg: 'secondary', label: status };
    return <Badge bg={v.bg}>{v.label}</Badge>;
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" style={{ color: colors.goldMid }} />
        <p className="mt-3">Loading Speaking Topics...</p>
      </Container>
    );
  }

  // DETAIL VIEW
  if (view === 'detail' && detailTopic) {
    const takeaways = detailTopic.key_takeaways || [];
    const outline = detailTopic.outline || [];
    const oneSheet = detailTopic.one_sheet_data || {};

    return (
      <Container fluid className="py-4 px-4">
        <Button variant="link" className="mb-3 ps-0" onClick={() => setView('list')}>
          &larr; Back to Speaking Topics
        </Button>

        <Card className="shadow-sm">
          <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="mb-0">{detailTopic.title}</h4>
                {detailTopic.subtitle && (
                  <small className="text-white-50">{detailTopic.subtitle}</small>
                )}
              </div>
              <div className="d-flex gap-2">
                {getTalkTypeBadge(detailTopic.talk_type)}
                {getStatusBadge(detailTopic.status)}
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            {/* Description */}
            {detailTopic.description && (
              <div className="mb-4">
                <h6>About This Talk</h6>
                <p>{detailTopic.description}</p>
              </div>
            )}

            {/* Key Takeaways */}
            {takeaways.length > 0 && (
              <div className="mb-4">
                <h6>Key Takeaways</h6>
                <ul>
                  {takeaways.map((t, i) => (
                    <li key={i}>{t}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Audience */}
            {detailTopic.ideal_audience && (
              <div className="mb-4">
                <h6>Ideal Audience</h6>
                <p>{detailTopic.ideal_audience}</p>
              </div>
            )}

            {/* Talk Outline */}
            {outline.length > 0 && (
              <div className="mb-4">
                <h6>Talk Outline</h6>
                <ListGroup>
                  {outline.map((section, i) => (
                    <ListGroup.Item key={i}>
                      <strong>{section.section}</strong> ({section.duration})
                      <br />
                      <small className="text-muted">{section.content}</small>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>
            )}

            {/* Speaker Bio (if one-sheet generated) */}
            {detailTopic.speaker_bio && (
              <div className="mb-4">
                <h6>Speaker Bio</h6>
                <p>{detailTopic.speaker_bio}</p>
              </div>
            )}

            {/* Why Book (if one-sheet generated) */}
            {oneSheet.why_book?.length > 0 && (
              <div className="mb-4">
                <h6>Why Book This Speaker</h6>
                <ul>
                  {oneSheet.why_book.map((reason, i) => (
                    <li key={i}>{reason}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Actions */}
            <div className="d-flex gap-2 flex-wrap mt-4">
              {detailTopic.status === 'draft' ? (
                <Button
                  variant="dark"
                  onClick={handleGenerateOneSheet}
                  disabled={generatingOneSheet}
                  style={{ backgroundColor: colors.greyDarkest }}
                >
                  {generatingOneSheet ? (
                    <><Spinner size="sm" className="me-2" /> Generating...</>
                  ) : (
                    'Generate Speaker One-Sheet'
                  )}
                </Button>
              ) : (
                <>
                  <Button
                    variant="primary"
                    onClick={() => handleExport(detailTopic.id)}
                  >
                    Download One-Sheet
                  </Button>
                  <Button
                    variant="outline-primary"
                    onClick={handleGenerateOneSheet}
                    disabled={generatingOneSheet}
                  >
                    {generatingOneSheet ? 'Regenerating...' : 'Regenerate'}
                  </Button>
                </>
              )}
              <Button
                variant="outline-danger"
                onClick={() => {
                  setDeleteId(detailTopic.id);
                  setShowDeleteModal(true);
                }}
              >
                Delete
              </Button>
            </div>
          </Card.Body>
        </Card>

        {/* Delete Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Delete Speaking Topic</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete this speaking topic? This cannot be undone.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Delete
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    );
  }

  // PICK VIEW - Choose from generated topics
  if (view === 'pick') {
    return (
      <Container fluid className="py-4 px-4">
        <Button variant="link" className="mb-3 ps-0" onClick={() => setView('generate')}>
          &larr; Back
        </Button>

        <Card className="shadow">
          <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
            <h4 className="mb-0">Choose a Speaking Topic</h4>
          </Card.Header>
          <Card.Body>
            <p className="text-muted mb-4">
              These topics were generated from your IP. Select one to develop into a full speaker one-sheet.
            </p>

            <Row>
              {generatedTopics.map((topic, idx) => (
                <Col lg={6} key={idx} className="mb-4">
                  <Card className="h-100">
                    <Card.Body>
                      <h5>{topic.title}</h5>
                      {topic.subtitle && (
                        <p className="text-muted small">{topic.subtitle}</p>
                      )}
                      <p>{topic.description}</p>

                      <h6 className="mt-3">Key Takeaways:</h6>
                      <ul className="small">
                        {topic.key_takeaways?.map((t, i) => (
                          <li key={i}>{t}</li>
                        ))}
                      </ul>

                      <p className="small text-muted mb-2">
                        <strong>Ideal for:</strong> {topic.ideal_audience}
                      </p>

                      {topic.based_on_framework && (
                        <Badge bg="info" className="mb-3">
                          Based on: {topic.based_on_framework}
                        </Badge>
                      )}

                      <div className="mt-3">
                        <Button
                          variant="dark"
                          onClick={() => handleSelectTopic(topic)}
                          style={{ backgroundColor: colors.greyDarkest }}
                        >
                          Develop This Topic
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>

            <div className="text-center mt-4">
              <Button variant="outline-secondary" onClick={() => setView('generate')}>
                Generate Different Topics
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // GENERATE VIEW
  if (view === 'generate') {
    return (
      <Container fluid className="py-4 px-4">
        <Button variant="link" className="mb-3 ps-0" onClick={() => setView('list')}>
          &larr; Back to Speaking Topics
        </Button>

        <Card className="shadow">
          <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
            <h4 className="mb-0">Generate Speaking Topics</h4>
          </Card.Header>
          <Card.Body>
            <p className="text-muted mb-4">
              We'll analyze your IP and generate compelling speaking topic ideas tailored to the format you choose.
            </p>

            <Form.Group className="mb-4">
              <Form.Label><strong>What type of talk are you developing?</strong></Form.Label>
              <Row>
                {[
                  { value: 'keynote', label: 'Keynote', desc: '20-45 min, inspirational, big ideas' },
                  { value: 'workshop', label: 'Workshop', desc: '90 min - half day, hands-on, practical' },
                  { value: 'webinar', label: 'Webinar', desc: '45-60 min, educational, actionable' },
                  { value: 'breakout', label: 'Breakout Session', desc: '45-60 min, focused topic' },
                  { value: 'panel', label: 'Panel Topic', desc: '30-45 min, discussion-worthy' }
                ].map(type => (
                  <Col md={4} key={type.value} className="mb-2">
                    <Card
                      className={`h-100 ${talkType === type.value ? 'border-primary' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setTalkType(type.value)}
                    >
                      <Card.Body className="text-center py-3">
                        <Form.Check
                          type="radio"
                          name="talkType"
                          checked={talkType === type.value}
                          onChange={() => setTalkType(type.value)}
                          label=""
                          className="d-none"
                        />
                        <strong>{type.label}</strong>
                        <br />
                        <small className="text-muted">{type.desc}</small>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Form.Group>

            <Button
              variant="dark"
              onClick={handleGenerate}
              disabled={generating}
              style={{ backgroundColor: colors.greyDarkest }}
            >
              {generating ? (
                <><Spinner size="sm" className="me-2" /> Generating Ideas...</>
              ) : (
                'Generate Topic Ideas'
              )}
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // LIST VIEW (default)
  return (
    <Container fluid className="py-4 px-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1>Speaking Topics</h1>
          <p className="text-muted mb-0">
            Develop speaking topics from your IP and generate speaker one-sheets that get you booked.
          </p>
        </div>
        <Button
          variant="dark"
          onClick={() => setView('generate')}
          style={{ backgroundColor: colors.greyDarkest }}
        >
          + New Topic
        </Button>
      </div>

      {/* No IP Warning Modal */}
      <Modal show={showNoIPWarning} onHide={() => setShowNoIPWarning(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Extract Your IP First</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Speaking topics work best when generated from your documented intellectual property.
          </p>
          <p className="mb-0">
            Please complete <strong>Module 2: Mind to Method</strong> first to extract your IP,
            then return here to develop speaking topics.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNoIPWarning(false)}>
            Close
          </Button>
          <Button
            variant="dark"
            onClick={() => navigate('/command-center/authority-engineer/extract')}
            style={{ backgroundColor: colors.greyDarkest }}
          >
            Extract IP Now
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Empty State */}
      {topics.length === 0 ? (
        <Card className="shadow-sm text-center py-5">
          <Card.Body>
            <div style={{ fontSize: '4rem', opacity: 0.3 }}>&#127908;</div>
            <h4 className="mt-3">No Speaking Topics Yet</h4>
            <p className="text-muted mb-4">
              Generate speaking topic ideas from your IP and create professional one-sheets.
            </p>
            <Button
              variant="dark"
              onClick={() => setView('generate')}
              style={{ backgroundColor: colors.greyDarkest }}
            >
              Generate Your First Topics
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Row>
          {topics.map(topic => (
            <Col lg={6} key={topic.id} className="mb-4">
              <Card className="h-100 shadow-sm">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h5 className="mb-0">{topic.title}</h5>
                    <div className="d-flex gap-1">
                      {getTalkTypeBadge(topic.talk_type)}
                      {getStatusBadge(topic.status)}
                    </div>
                  </div>

                  {topic.subtitle && (
                    <p className="text-muted small mb-2">{topic.subtitle}</p>
                  )}

                  {topic.ideal_audience && (
                    <p className="small mb-2">
                      <strong>Audience:</strong> {topic.ideal_audience}
                    </p>
                  )}

                  {topic.framework_name && (
                    <Badge bg="light" text="dark" className="mb-2">
                      Based on: {topic.framework_name}
                    </Badge>
                  )}

                  <div className="d-flex gap-2 mt-3">
                    <Button
                      variant="outline-dark"
                      size="sm"
                      onClick={() => viewTopicDetail(topic.id)}
                    >
                      View
                    </Button>
                    {topic.status === 'ready' && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleExport(topic.id)}
                      >
                        Download
                      </Button>
                    )}
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => {
                        setDeleteId(topic.id);
                        setShowDeleteModal(true);
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Info Card */}
      <Card className="mt-4 border-0" style={{ backgroundColor: '#fff8e6' }}>
        <Card.Body>
          <h6 className="mb-2" style={{ color: colors.goldDark }}>
            <span className="me-2">&#127908;</span>
            Why Speaking Builds Authority
          </h6>
          <p className="small text-muted mb-0">
            Speaking positions you as the go-to expert. A single keynote can generate more qualified leads
            than months of content marketing. Your speaker one-sheet is your pitch document - make it count.
          </p>
        </Card.Body>
      </Card>

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Speaking Topic</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this speaking topic? This cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default SpeakingTopics;
